/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { jsx } from '@atlaskit/css';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	type ActionKeys,
	REQUIRE_INFORMATION_TOOL,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ProjectSelector } from '@atlassian/conversation-assistant-ui-components/ui/ProjectSelector';

const assertIsRequireInformationAction = (action: MessageAction): MessageAction => {
	if (action.key !== REQUIRE_INFORMATION_TOOL) {
		throw new Error('Invalid action key for RequireInformationTool');
	}
	return action;
};

export const filterRequireInformationActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.RequireInformationTool]>[] => {
	return actions.filter(assertIsRequireInformationAction) as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.RequireInformationTool]
	>[];
};

export const RequireInformationUIAction = ({
	actions,
	cloudId,
	productKey,
}: ActionRendererFor<typeof ActionKeys.RequireInformationTool>): JSX.Element | null => {
	di(ProjectSelector, usePublish);

	const publish = usePublish('ai-mate');
	const firstAction = actions[0];

	const sendConfirmMessage = useCallback(
		(message: string) => {
			publish({
				type: 'message-send',
				source: 'ai-mate',
				data: {
					prompt: message,
					productKey,
				},
			});
		},
		[publish, productKey],
	);

	const renderTool = () => {
		switch (firstAction.data?.toolName) {
			case 'JiraProjectSearchTool':
				return (
					<ProjectSelector
						siteId={cloudId}
						setProject={({ projectName }) => sendConfirmMessage(projectName)}
					/>
				);
			default:
				return null;
		}
	};

	return renderTool();
};
